import { z } from 'zod';

/* =========================================================================
 * Content schemas — Course → Unit → Skill → Lesson → Exercise
 *
 * Zod schemas validate every .json file under `content/`. The schemas are
 * also the single source of truth for TS types consumed by the lesson
 * engine (@fluent/app).
 *
 * File → schema mapping (by filename):
 *   course.json    → CourseSchema
 *   unit.json      → UnitSchema
 *   skill.json     → SkillSchema
 *   lesson-*.json  → LessonSchema
 *
 * Slug rules (id fields):
 *   - kebab-case, lowercase, ASCII only
 *   - stable — once published, IDs never change (progress rows reference them)
 * ========================================================================= */

/** Language codes used in this course. `ka` = Georgian (UI), `en` = English (what they learn). */
export const localeSchema = z.enum(['ka', 'en']);
export type ContentLocale = z.infer<typeof localeSchema>;

/** Stable opaque slug. Lowercase ASCII + digits + hyphens. */
const slugSchema = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/u, 'id must be kebab-case lowercase ASCII');

/** Audio file key — stable slug that maps 1:1 to an mp3 in the R2 bucket.
 * Pattern: `<level>-<unit>-<skill>-<phrase>.mp3` e.g. `a1-u01-s01-hello.mp3`. */
const audioKeySchema = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*\.mp3$/u, 'audio_key must be kebab-case and end with .mp3');

/* ------------------------- Exercise types ------------------------- */

/** Speaker tags allowed in dialogue lines and on character-voiced cards.
 *
 * Locked 2026-04-29 (DECISIONS.md): single character voice per speaker
 * across the entire app — Giorgi → echo, Mariam → alloy. The `tts-generate`
 * pipeline reads `speaker` and uses the corresponding voice;
 * the renderer color-codes the speaker tag (Giorgi green, Mariam terracotta)
 * so the dialogue reads as a real conversation, not a wall of text.
 *
 * (Renamed Mariam → Nini on 2026-06-03, then reverted Nini → Mariam on
 * 2026-09-01, both at Tornike's request. The name is Mariam.)
 *
 * The renderer lowercases this value into `data-speaker`, which Dialogue.css
 * and ReviewCard.css select on — rename both together or the accent silently
 * disappears.
 *
 * Add new speakers here when the curriculum needs them — and lock their
 * voice in DECISIONS.md before authoring any line under that name. */
export const speakerSchema = z.enum(['Giorgi', 'Mariam']);

/** PhraseIntro — first-exposure teaching card. Shows the English phrase,
 * its Georgian gloss, and the audio. No fail state — user reads, listens,
 * taps "got it" to advance. Used as the teaching moment BEFORE any harder
 * exercise tests the same phrase.
 *
 * CONTENT-BRIEF rule: every new phrase must appear in a phrase_intro
 * within the same lesson before any exercise tests it. This is the
 * difference between teaching and testing. */
export const phraseIntroSchema = z.object({
  type: z.literal('phrase_intro'),
  id: slugSchema,
  en: z.string().min(1), // English phrase being taught
  ka: z.string().min(1), // Georgian gloss
  audio_key: audioKeySchema, // required — first-exposure pronunciation matters
  // Optional speaker. When the card is a named character's own speech
  // (e.g. Giorgi saying "I am Giorgi"), set this so the TTS pipeline voices
  // it with that character's locked voice instead of the default narrator
  // (alloy). Leave unset for neutral narration. Locked 2026-06-04 (Tornike:
  // "match the speaker").
  speaker: speakerSchema.optional(),
  // tiny Georgian note (e.g. "polite phrase") — 80-char ceiling locked in
  // LESSON-FORMAT-PROPOSAL.md so notes stay one-line subtitles, never
  // textbook explainers (anti-pattern from CONTENT-BRIEF.md).
  note_ka: z.string().max(80, 'note_ka must be ≤ 80 chars').optional(),
  /* ---- Phrases course (ფრაზები, 2026-10-18 — PHRASES.md § 4) ----
   * A phrase needs context to be learned, so phrase cards in the phrases
   * course carry one example sentence (EN + KA + its own clip), the
   * phrase's register and — for idioms whose words mislead — the
   * word-for-word Georgian reading as a trap warning. All optional so the
   * main course's 1,448 cards are untouched; the validator makes them
   * required inside the phrases course. */
  example_en: z.string().min(1).optional(),
  example_ka: z.string().min(1).optional(),
  example_audio_key: audioKeySchema.optional(),
  literal_ka: z.string().max(60, 'literal_ka must be ≤ 60 chars').optional(),
  register: z.enum(['neutral', 'informal', 'formal', 'slang']).optional(),
});

/** TranslateTyped — show a Georgian prompt, user types the English answer.
 * Matcher is case-insensitive, whitespace-trimmed; alt_answers widen the set. */
export const translateTypedSchema = z.object({
  type: z.literal('translate_typed'),
  id: slugSchema,
  prompt: z.string().min(1), // Georgian prompt
  answer: z.string().min(1), // English canonical answer
  alt_answers: z.array(z.string().min(1)).default([]),
  audio_key: audioKeySchema.optional(),
});

/** TranslateTap — show a Georgian prompt, user taps English word tokens to build
 * the answer sentence. `answer_tokens` is the exact correct order; `distractors`
 * mix into the token pool to make it non-trivial. */
export const translateTapSchema = z.object({
  type: z.literal('translate_tap'),
  id: slugSchema,
  prompt: z.string().min(1),
  answer_tokens: z.array(z.string().min(1)).min(2),
  distractors: z.array(z.string().min(1)).default([]),
  audio_key: audioKeySchema.optional(),
});

/** ListenType — audio-only prompt (no text shown), user types the English phrase.
 * `translation_ka` is shown as a hint after the user answers. */
export const listenTypeSchema = z.object({
  type: z.literal('listen_type'),
  id: slugSchema,
  audio_key: audioKeySchema,
  answer: z.string().min(1),
  alt_answers: z.array(z.string().min(1)).default([]),
  translation_ka: z.string().optional(),
});

/** TapPairs — two columns (English / Georgian), user taps matching pairs. */
export const tapPairsSchema = z.object({
  type: z.literal('tap_pairs'),
  id: slugSchema,
  pairs: z
    .array(
      z.object({
        en: z.string().min(1),
        ka: z.string().min(1),
        audio_key: audioKeySchema.optional(),
      }),
    )
    .min(3, 'tap_pairs needs at least 3 pairs')
    .max(8, 'tap_pairs should have at most 8 pairs (readable on mobile)'),
});

/** Dialogue — multi-line conversation between two named speakers. Each line
 *  has its own audio_key (per-speaker voice in the TTS pipeline) and renders
 *  on its own row with a colored speaker tag. Auto-plays sequentially with
 *  ~500ms pauses between lines. No-fail (same engine semantics as
 *  `phrase_intro` — counts toward neither XP nor accuracy).
 *
 *  Phase 12 use case: the wrap-up moment that stitches a lesson's phrases
 *  into a real conversation (LESSON-FORMAT-PROPOSAL.md § Phase E). */
export const dialogueSchema = z.object({
  type: z.literal('dialogue'),
  id: slugSchema,
  lines: z
    .array(
      z.object({
        speaker: speakerSchema,
        en: z.string().min(1),
        ka: z.string().min(1),
        audio_key: audioKeySchema, // required — per-line audio with the speaker's voice
      }),
    )
    .min(2, 'dialogue needs at least 2 lines (otherwise use phrase_intro)')
    .max(8, 'dialogue should have at most 8 lines (longer = lesson-shaped, not wrap-shaped)'),
  /** Optional Georgian note shown beneath the dialogue header — same 80-char
   *  ceiling as phrase_intro.note_ka. Use it for cultural context when needed. */
  note_ka: z.string().max(80, 'note_ka must be ≤ 80 chars').optional(),
});

/** why_ka — ONE short Georgian line that names the rule behind a choice
 *  exercise's answer ("he / she / it-თან ზმნას -s ემატება: he works.").
 *  Shown in the result bar only when the learner picks a wrong option.
 *  Same idea as the exam simulator's grammar `why_ka` (DECISIONS.md
 *  2026-09-25). Added 2026-10-07 — GRAMMAR-REVIEW-2026-10-07 § 2. The
 *  validator requires it on every cloze / choose_correct. */
export const WHY_KA_MAX = 130;
const whyKaSchema = z.string().min(1).max(WHY_KA_MAX, `why_ka must be ≤ ${WHY_KA_MAX} chars`);

/** ChooseCorrect — show a Georgian prompt; user taps the correct English
 *  from 3–4 options. Lower cognitive load than `translate_typed` (recognition
 *  before production). Distractors are intentionally chosen to teach common
 *  mistakes — e.g., wrong "to be" conjugation ("I are Giorgi"). The correct
 *  answer MUST appear verbatim in `options` — enforced by the validator
 *  script (Zod discriminated unions can't host .refine, so the cross-field
 *  rule lives in scripts/validate-content.ts § checkOptionConsistency). */
export const chooseCorrectSchema = z.object({
  type: z.literal('choose_correct'),
  id: slugSchema,
  prompt: z.string().min(1), // Georgian prompt
  answer: z.string().min(1),
  options: z.array(z.string().min(1)).min(2).max(4),
  why_ka: whyKaSchema.optional(),
  audio_key: audioKeySchema.optional(),
});

/** ListenChoose — audio prompt; user taps the correct English from options.
 *  Audio-focused recognition: ear-trained but no typing. Same shape as
 *  `choose_correct` but the prompt is the audio clip (no Georgian text
 *  shown). Validator enforces `answer` appears in `options`. */
export const listenChooseSchema = z.object({
  type: z.literal('listen_choose'),
  id: slugSchema,
  audio_key: audioKeySchema, // required — the prompt
  answer: z.string().min(1),
  options: z.array(z.string().min(1)).min(2).max(4),
  translation_ka: z.string().optional(),
});

/** Cloze — fill-in-the-blank exercise. Sentence with exactly one `___`
 *  blank; user picks the correct word from 2–3 options. Tests grammar
 *  precisely ("I am" vs "I is" vs "I are") in a way `translate_typed`
 *  can't.
 *
 *  Validator enforces:
 *   • `prompt_en` contains exactly one `___` token
 *   • `answer` appears verbatim in `options`. */
export const clozeSchema = z.object({
  type: z.literal('cloze'),
  id: slugSchema,
  prompt_en: z.string().min(1),
  prompt_ka: z.string().min(1), // Georgian translation of the full sentence (context)
  answer: z.string().min(1),
  options: z.array(z.string().min(1)).min(2).max(4),
  why_ka: whyKaSchema.optional(),
  audio_key: audioKeySchema.optional(),
});

/** PhraseChoice — the phrases course's test item (PHRASES.md § 5.1). One
 *  schema, eight variants; every variant is "tap one of 3–4 options":
 *
 *   meaning      EN phrase (prompt_en, + prompt_audio_key)  → Georgian meanings
 *   pick_phrase  Georgian meaning (prompt_ka)               → EN phrases
 *   situation    Georgian situation (prompt_ka)             → EN phrases
 *   gap          EN sentence / 2-line exchange with ONE ___ → EN phrases
 *   reply        a line someone says (prompt_en, speaker)   → EN replies
 *   paraphrase   plain EN sentence (prompt_en)              → EN phrases
 *   complete     the phrase with ONE word missing (___)     → single words
 *   odd_one_out  (instruction only)                         → EN phrases; 3 share a meaning
 *
 *  Options are Georgian for `meaning`, English for every other variant.
 *  `why_ka` is required (shown after the verdict). `after_ka` is Georgian
 *  revealed only AFTER the verdict (e.g. a gap sentence's translation —
 *  showing it before would give the meaning away). `audio_key` plays after
 *  the verdict: the full sentence with the answer in place, or the phrase.
 *  `phrases` lists the canonical `en` of every phrase the item tests (the
 *  validator's teach-before-test runs on it). Per-variant field rules live
 *  in scripts/validate-content.ts (Zod unions can't host .refine). */
export const PHRASE_CHOICE_VARIANTS = [
  'meaning',
  'pick_phrase',
  'situation',
  'gap',
  'reply',
  'paraphrase',
  'complete',
  'odd_one_out',
] as const;
export const phraseChoiceVariantSchema = z.enum(PHRASE_CHOICE_VARIANTS);
export const phraseChoiceSchema = z.object({
  type: z.literal('phrase_choice'),
  id: slugSchema,
  variant: phraseChoiceVariantSchema,
  prompt_en: z.string().min(1).optional(),
  prompt_ka: z.string().min(1).optional(),
  speaker: speakerSchema.optional(),
  prompt_audio_key: audioKeySchema.optional(),
  answer: z.string().min(1),
  options: z.array(z.string().min(1)).min(3).max(4),
  why_ka: whyKaSchema,
  after_ka: z.string().min(1).max(160, 'after_ka must be ≤ 160 chars').optional(),
  audio_key: audioKeySchema.optional(),
  phrases: z.array(z.string().min(1)).min(1),
});

/** GrammarTip — a short, no-fail teaching card that explains the ONE grammar
 *  point a skill covers, in Georgian, before the learner practices it. Placed
 *  as the first card of a skill's first lesson. Not a test (no XP, no accuracy);
 *  the learner reads it and taps "got it" to advance, same engine semantics as
 *  phrase_intro. Added 2026-06-14 — the "learn-by-doing only" v1 choice left
 *  Georgian-specific rules (articles, plurals, word order) under-explained. */
export const grammarTipSchema = z.object({
  type: z.literal('grammar_tip'),
  id: slugSchema,
  ka: z.string().min(1), // the tip, in Georgian (2-3 short sentences)
  title_ka: z.string().optional(), // optional short heading, e.g. "a / an"
  /** Optional ≤30 s explainer video for this tip (2026-10-08): the card shows
   *  a ▶ button that plays `${VITE_VIDEO_BASE}/<key>.mp4` (poster `<key>.jpg`).
   *  Upload with scripts/upload-video.mjs; a re-cut ships under a new key. */
  video_key: z
    .string()
    .regex(/^[a-z0-9-]+(\/[a-z0-9-]+)*$/, 'video_key: lowercase path without extension')
    .optional(),
});

export const exerciseSchema = z.discriminatedUnion('type', [
  grammarTipSchema,
  phraseIntroSchema,
  translateTypedSchema,
  translateTapSchema,
  listenTypeSchema,
  tapPairsSchema,
  dialogueSchema,
  chooseCorrectSchema,
  listenChooseSchema,
  clozeSchema,
  phraseChoiceSchema,
]);

export type GrammarTip = z.infer<typeof grammarTipSchema>;
export type PhraseIntro = z.infer<typeof phraseIntroSchema>;
export type TranslateTyped = z.infer<typeof translateTypedSchema>;
export type TranslateTap = z.infer<typeof translateTapSchema>;
export type ListenType = z.infer<typeof listenTypeSchema>;
export type TapPairs = z.infer<typeof tapPairsSchema>;
export type Dialogue = z.infer<typeof dialogueSchema>;
export type ChooseCorrect = z.infer<typeof chooseCorrectSchema>;
export type ListenChoose = z.infer<typeof listenChooseSchema>;
export type Cloze = z.infer<typeof clozeSchema>;
export type PhraseChoice = z.infer<typeof phraseChoiceSchema>;
export type PhraseChoiceVariant = z.infer<typeof phraseChoiceVariantSchema>;
export type PhraseRegister = NonNullable<PhraseIntro['register']>;
export type Speaker = z.infer<typeof speakerSchema>;
export type Exercise = z.infer<typeof exerciseSchema>;
export type ExerciseType = Exercise['type'];

/* ---------------------- Lesson / Skill / Unit / Course --------------------- */

export const lessonSchema = z.object({
  kind: z.literal('lesson'),
  id: slugSchema,
  title: z.string().min(1), // Georgian
  title_en: z.string().min(1), // English, for logs + admin
  /** Phrases course only (PHRASES.md): the lesson's catchiest phrase, shown
   *  on its tile on the ფრაზები screen. Must be the `en` of one of the
   *  lesson's phrase cards (validator). */
  tile_en: z.string().min(1).optional(),
  /** ~7–12 min typical — 3 to 30 exercises per lesson. The upper bound
   *  covers the locked Phase 12 24-ex template + headroom for richer
   *  lessons. Going past 30 means the lesson is too long for one
   *  cognitive session; split it across two. */
  exercises: z.array(exerciseSchema).min(3).max(30),
});

export const skillSchema = z.object({
  kind: z.literal('skill'),
  id: slugSchema,
  title: z.string().min(1),
  title_en: z.string().min(1),
  description: z.string().optional(),
  /** Ordered lesson IDs within this skill. First is the unlock anchor. */
  lessons: z.array(slugSchema).min(1).max(8),
});

export const unitSchema = z.object({
  kind: z.literal('unit'),
  id: slugSchema,
  title: z.string().min(1),
  title_en: z.string().min(1),
  description: z.string().optional(),
  /** Ordered skill IDs. */
  skills: z.array(slugSchema).min(1),
});

export const courseSchema = z.object({
  kind: z.literal('course'),
  id: slugSchema,
  title: z.string().min(1),
  source_locale: z.literal('ka'),
  target_locale: z.literal('en'),
  /** Ordered unit IDs across the whole course. */
  units: z.array(slugSchema).min(1),
});

export type Lesson = z.infer<typeof lessonSchema>;
export type Skill = z.infer<typeof skillSchema>;
export type Unit = z.infer<typeof unitSchema>;
export type Course = z.infer<typeof courseSchema>;

/** Tagged union of every content document shape. Discriminated by `kind`
 * so a single schema validates any .json file once we route by filename. */
export const contentDocumentSchema = z.discriminatedUnion('kind', [
  courseSchema,
  unitSchema,
  skillSchema,
  lessonSchema,
]);
export type ContentDocument = z.infer<typeof contentDocumentSchema>;
